import { useState } from "react";
import API from "../../lib/api";
import { Plus, Trash, PencilSimple, Ticket } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../../components/ui/dialog";

const EMPTY_FORM = { code: "", type: "percent", value: "", min_subtotal: "0", usage_limit: "", expires_at: "", active: true };

export default function DiscountsTab({ discounts, onReload }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModal(true); };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      code: d.code, type: d.type, value: String(d.value),
      min_subtotal: String(d.min_subtotal || 0),
      usage_limit: d.usage_limit ? String(d.usage_limit) : "",
      expires_at: d.expires_at ? d.expires_at.slice(0, 10) : "",
      active: d.active !== false
    });
    setModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        type: form.type,
        value: parseFloat(form.value) || 0,
        min_subtotal: parseFloat(form.min_subtotal) || 0,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
        expires_at: form.expires_at ? `${form.expires_at}T23:59:59+00:00` : null,
        active: form.active
      };
      if (editing) {
        await API.put(`/admin/discounts/${editing.id}`, payload);
      } else {
        await API.post("/admin/discounts", payload);
      }
      setModal(false);
      onReload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Failed to save discount code");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this discount code? Customers will no longer be able to use it.")) return;
    try {
      await API.delete(`/admin/discounts/${id}`);
      onReload();
    } catch (err) {
      console.error("Failed to delete discount:", err);
    }
  };

  const describeValue = (d) => d.type === "percent" ? `${d.value}% off` : `$${d.value.toFixed(2)} off`;

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-sm text-[#A1A1AA] font-light">{discounts.length} discount codes</p>
          <p className="text-xs text-zinc-600 font-light mt-1">Create promo codes for launches, holidays, and VIP customers.</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300" data-testid="add-discount-btn">
          <Plus size={14} /> Add Code
        </button>
      </div>
      <div className="space-y-3" data-testid="admin-discounts-list">
        {discounts.map(d => (
          <div key={d.id} className="flex items-center gap-4 bg-[#121212] border border-[rgba(212,175,55,0.1)] p-5" data-testid={`admin-discount-${d.id}`}>
            <Ticket size={18} weight="thin" className="text-[#D4AF37] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-[#FDFBF7] font-mono tracking-wider">{d.code}</p>
                <span className="text-[10px] text-[#D4AF37] bg-[#0a0a0a] px-2 py-0.5 uppercase tracking-wider">{describeValue(d)}</span>
                {!d.active && <span className="text-[10px] text-red-400 border border-red-400/30 px-2 py-0.5 uppercase tracking-wider">Inactive</span>}
                {d.expires_at && <span className="text-[10px] text-[#A1A1AA] border border-white/10 px-2 py-0.5">Expires {d.expires_at.slice(0, 10)}</span>}
              </div>
              <p className="text-xs text-[#A1A1AA] font-light mt-1">
                {d.min_subtotal > 0 && `Min order $${d.min_subtotal.toFixed(2)} · `}
                Used {d.times_used || 0}{d.usage_limit ? ` / ${d.usage_limit}` : ""} times
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => openEdit(d)} className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors p-2" data-testid={`edit-discount-${d.id}`}><PencilSimple size={16} weight="thin" /></button>
              <button onClick={() => handleDelete(d.id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors p-2" data-testid={`delete-discount-${d.id}`}><Trash size={16} weight="thin" /></button>
            </div>
          </div>
        ))}
        {discounts.length === 0 && (
          <p className="text-[#A1A1AA] text-sm font-light py-8 text-center">No discount codes yet. Create one for your next launch or holiday sale.</p>
        )}
      </div>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-[#121212] border-[rgba(212,175,55,0.2)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7]">
              {editing ? "Edit Discount Code" : "Add Discount Code"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4" data-testid="discount-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Code</label>
              <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-mono tracking-wider" placeholder="e.g. SLAY20" data-testid="discount-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Type</label>
                <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="discount-type-select">
                  <option value="percent">Percent off (%)</option>
                  <option value="fixed">Fixed amount ($)</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">{form.type === "percent" ? "Percent" : "Amount ($)"}</label>
                <input type="number" min="0" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" placeholder={form.type === "percent" ? "20" : "10.00"} data-testid="discount-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Min Order ($)</label>
                <input type="number" min="0" value={form.min_subtotal} onChange={e => setForm(p => ({ ...p, min_subtotal: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="discount-min-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Usage Limit</label>
                <input type="number" min="0" value={form.usage_limit} onChange={e => setForm(p => ({ ...p, usage_limit: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" placeholder="Unlimited" data-testid="discount-limit-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Expires On</label>
                <input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="discount-expires-input" />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] font-light cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} className="accent-[#D4AF37]" data-testid="discount-active-checkbox" /> Active
                </label>
              </div>
            </div>
            <button onClick={handleSave} disabled={saving || !form.code || !form.value} className="w-full bg-[#D4AF37] text-black py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300 disabled:opacity-50 mt-4" data-testid="save-discount-btn">
              {saving ? "Saving..." : editing ? "Update Code" : "Create Code"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
